import type {
  AiActionCapabilityV1,
  AiActionPolicyV1,
  SmartCreationReviewBidBoundsSuggestion,
  SmartCreationReviewEditChange,
} from "@adpilot/shared";
import { IconChevronDown, IconInfoCircle } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AiActionPolicyFields } from "@/components/ai/AiActionPolicyFields";
import { Button } from "@/components/ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface ReviewAiActionSpaceProps {
  policy?: AiActionPolicyV1;
  capabilities?: readonly AiActionCapabilityV1[];
  capabilityStatus?: "loading" | "ready" | "unavailable";
  disabled?: boolean;
  onApply?: (changes: SmartCreationReviewEditChange[]) => Promise<boolean>;
  variant?: "default" | "preview";
  immediate?: boolean;
  bidBoundsSuggestion?: SmartCreationReviewBidBoundsSuggestion;
  onInvalidChange?: (invalid: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onRetryCapabilities?: () => void;
  currencyCode?: string;
}

/** The task applies these permissions to its managed group after all resources are ready. */
export function ReviewAiActionSpace(props: ReviewAiActionSpaceProps) {
  return props.variant === "preview" ? (
    <PreviewAiActionSpace {...props} />
  ) : (
    <CollapsibleAiActionSpace {...props} />
  );
}

function CollapsibleAiActionSpace(props: ReviewAiActionSpaceProps) {
  const { t } = useTranslation("smartCreation");
  const [expanded, setExpanded] = useState(false);
  const apply = props.onApply;
  return (
    <section
      className="flex flex-col gap-3 border-t border-line px-5 py-3"
      data-review-ai-action-space
    >
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1 px-1"
          aria-expanded={expanded}
          aria-controls="review-ai-action-space-content"
          onClick={() => setExpanded((current) => !current)}
        >
          <IconChevronDown data-icon="inline-start" className={cn(!expanded && "-rotate-90")} />
          {t("review.aiActions.title")}
        </Button>
        <Popover>
          <PopoverTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("review.aiActions.availability")}
              />
            }
          >
            <IconInfoCircle />
          </PopoverTrigger>
          <PopoverPopup className="smart-creation-preview max-w-80">
            <p className="text-xs leading-5">{t("review.aiActions.scopeDescription")}</p>
          </PopoverPopup>
        </Popover>
      </div>
      {expanded ? (
        <div id="review-ai-action-space-content" className="flex flex-col gap-3">
          <p className="text-xs text-muted-foreground">{t("review.aiActions.permissionSummary")}</p>
          <AiActionPolicyFields
            {...props}
            disabled={props.disabled || props.policy?.source !== "smart_creation"}
            onPolicyChange={
              apply ? (policy) => apply([{ operation: "set_ai_action_policy", policy }]) : undefined
            }
          />
        </div>
      ) : null}
    </section>
  );
}

function PreviewAiActionSpace(props: ReviewAiActionSpaceProps) {
  const { t, i18n } = useTranslation("smartCreation");
  const policy = props.policy;
  const apply = props.onApply;
  const fields = {
    immediate: props.immediate,
    bidBoundsSuggestion: props.bidBoundsSuggestion,
    onInvalidChange: props.onInvalidChange,
    policy,
    capabilities: props.capabilities,
    capabilityStatus: props.capabilityStatus,
    disabled: props.disabled || policy?.source !== "smart_creation",
    onPolicyChange: apply
      ? (next: AiActionPolicyV1) => apply([{ operation: "set_ai_action_policy", policy: next }])
      : undefined,
    onRetryCapabilities: props.onRetryCapabilities,
  };
  const currency = (amount: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: props.currencyCode ?? "USD",
    }).format(amount);
  const boundaryRows = [
    {
      label: t("review.preview.ai.bidRange"),
      value: policy?.bidBounds
        ? `${currency(policy.bidBounds.min)} – ${currency(policy.bidBounds.max)}`
        : t("review.preview.ai.unset"),
    },
    {
      label: t("review.preview.ai.boostBoundary"),
      value: policy?.budgetBoostPercentLimit
        ? t("review.preview.ai.boostBoundaryValue", {
            percent: policy.budgetBoostPercentLimit,
            days: policy.budgetBoostDurationDays,
          })
        : t("review.preview.ai.unset"),
    },
    {
      label: t("review.preview.ai.exclusionCounts"),
      value: t("review.preview.ai.exclusionCountsValue", {
        harvest: policy?.harvestExclusions.length ?? 0,
        negative: policy?.negativeExclusions.length ?? 0,
      }),
    },
  ];
  return (
    <section
      className="grid items-start gap-5 pt-5 lg:grid-cols-[minmax(0,1fr)_410px]"
      data-review-ai-action-space
    >
      <AiActionPolicyFields {...fields} variant="preview" onDirtyChange={props.onDirtyChange} />
      <aside className="flex min-w-0 flex-col gap-4 rounded-[10px] border border-line bg-surface p-5">
        <h3 className="text-sm font-semibold leading-5">{t("review.preview.ai.boundaries")}</h3>
        <dl className="flex flex-col gap-4">
          {boundaryRows.map((row) => (
            <div
              key={row.label}
              className="flex items-center justify-between gap-3 text-xs leading-[18px]"
            >
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd className="text-right font-medium">{row.value}</dd>
            </div>
          ))}
        </dl>
      </aside>
    </section>
  );
}
